import React, { useEffect, useState } from "react";
import useMediaQueries from "hooks/useMediaQueries.js";
import MobileUploaderContainer from "../MobileUploader/MobileUploaderContainer.js";
import Image from "components/Elements/Image/Image.tsx";
import ArtistPageElement from "./ArtistPageElement.js";
import UploaderLogicWrapper from "./UploaderLogicWrapper.js";
import HomeBrandingElement from "./HomeBrandingElement.tsx";
import AdBackground from "components/Reusable/AdBackground/index.tsx";
import AutoPlayVideo from "components/Reusable/AutoPlayVideo/index.tsx";
import useBgMinHeight from "../hooks/useBgMinHeight.ts";
import useBackground from "hooks/useBackground.ts";
import { useUploaderLogic } from "../Helpers/useUploaderLogic.js";
import { useContent } from "contexts/contentContext/contentContext.js";
import { DropZone } from "./DropZone.js";
import { UploaderComponent } from "./UploaderComponent.js";
import { useUploader } from "contexts/uploaderContext/uploaderContext.js";
import { useUser } from "contexts/userContext/userContext.js";
import useCampaignViewCounts from "hooks/useCampaignViewCounts.ts";
import ThanksActionButton from "components/Reusable/ThanksActionButton/index.tsx";

import "./uploader.scss";

export const UploaderContext = React.createContext();

const IS_PREVIEW = window.location.pathname.includes("preview-ad-");

export const HomeUploader = () => {
  const { getRecipientEmail, getOpenUploader, uploaderGetFiles } =
    useUploader();
  const {
    getDefaultBackgroundImage,
    getArtist,
    getAdsBackground,
    getRandBackgroundRedirectUrl,
    getDirection,
  } = useContent();
  const { mobileCondition, desktopCondition } = useMediaQueries();
  const { filesCount, filesProgress } = useUploaderLogic();
  const { getUserData, isSubscribed } = useUser();
  const showUploader =
    uploaderGetFiles().length > 0 || getRecipientEmail() || getOpenUploader();
  const { updateAdClickCount } = useBackground();
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  useCampaignViewCounts();

  const { getMinHeight } = useBgMinHeight();

  const UserBackgroundCoverImage = getUserData().UserBackgroundCoverImage;

  const showMobileUploader = mobileCondition && showUploader;

  const handleBannerClick = async () => {
    if (
      getAdsBackground().redirectUrl &&
      !IS_PREVIEW &&
      !UserBackgroundCoverImage
    ) {
      await updateAdClickCount();
    }
  };

  useEffect(() => {
    // closing sidebar notification when opening uploader
    if (showUploader) {
      document
        .querySelector(".modal-side-notification")
        ?.querySelector(".modal-close-button")
        ?.click();
    }
  }, [showUploader]);

  const backgroundCoverImage = getUserData().SetCoverOnNewHomePage
    ? UserBackgroundCoverImage || getDefaultBackgroundImage()
    : getDefaultBackgroundImage();

  const conditionToHideArtistPageElement =
    (getUserData().SetCoverOnNewHomePage &&
      getUserData().UserBackgroundCoverImage) ||
    !getArtist() ||
    getOpenUploader();
  const [bgHeight, setBgHeight] = useState(0);

  // change bgHeight and bgYPos on window resize
  useEffect(() => {
    const handleResize = () => {
      if (
        (getAdsBackground()?.adId && getAdsBackground()?.isBlocked) ||
        IS_PREVIEW
      ) {
        const bgdImageEl = document.querySelector(".home-bgd-image");
        if (bgdImageEl) {
          setBgHeight(bgdImageEl.height);
        }
      }

      if (getAdsBackground()?.adId && !getAdsBackground()?.isBlocked) {
        const homeContainer = document.querySelector(".home-main-container");
        if (homeContainer) {
          homeContainer.style.minHeight = getMinHeight();
        }
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [
    getAdsBackground()?.adId,
    getAdsBackground()?.isBlocked,
    mobileCondition,
    getMinHeight(),
  ]);

  useEffect(() => {
    if (
      (getAdsBackground()?.isBlocked && getAdsBackground()?.adId) ||
      IS_PREVIEW
    ) {
      const homeContainer = document.querySelector(".home-main-container");
      if (homeContainer) {
        homeContainer.style.minHeight = "unset";
      }
    } else if (!getAdsBackground()?.isBlocked && getAdsBackground()?.adId) {
      const homeContainer = document.querySelector(".home-main-container");
      if (homeContainer) {
        homeContainer.style.minHeight = getMinHeight();
      }
    }
  }, [
    getAdsBackground()?.isBlocked,
    getAdsBackground()?.adId,
    mobileCondition,
    getMinHeight(),
  ]);

  useEffect(() => {
    if (UserBackgroundCoverImage) {
      const homeContainer = document.querySelector(".home-main-container");
      if (homeContainer) {
        homeContainer.style.minHeight = "100vh";
      }
      setBgHeight(0);
    } else {
      const homeContainer = document.querySelector(".home-main-container");
      if (homeContainer) {
        if (
          !getAdsBackground()?.isBlocked &&
          getAdsBackground()?.adId &&
          getAdsBackground()?.script
        ) {
          homeContainer.style.minHeight = getMinHeight();
        }
      }
    }
  }, [
    UserBackgroundCoverImage,
    getAdsBackground()?.isBlocked,
    getAdsBackground()?.adId,
    getAdsBackground()?.script,
    mobileCondition,
    getMinHeight(),
  ]);

  useEffect(() => {
    const updateThankYouButtonPosition = () => {
      const thankYouButton = document.getElementById("thankYouButton");
      const sidePanel = document.querySelector(".side-panel");

      if (!thankYouButton || !sidePanel) return;

      // Check if side panel is open
      const isSidePanelOpen = sidePanel.classList.contains("open");

      if (isSidePanelOpen && desktopCondition) {
        // Get the direction (RTL or LTR)
        const isRTL = getDirection() === "rtl";

        // Apply transform based on direction
        thankYouButton.style.transform = isRTL
          ? "translateX(65px)"
          : "translateX(-65px)";
      } else {
        // Reset position when panel is closed
        thankYouButton.style.transform = "translateX(0px)";
      }
    };

    updateThankYouButtonPosition();

    const sidePanelObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          updateThankYouButtonPosition();
        }
      });
    });

    const sidePanel = document.querySelector(".side-panel");
    if (sidePanel) {
      sidePanelObserver.observe(sidePanel, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }
  }, [desktopCondition, getDirection]);

  // const imgLinkCoords = mobileCondition
  //   ? getAdsBackground().mobileCoordinates
  //   : getAdsBackground()?.desktopCoordinates;

  return (
    <>
      {showMobileUploader ? null : backgroundCoverImage ||
        getAdsBackground() ? (
        <>
          {IS_PREVIEW ||
          getAdsBackground()?.isBlocked ||
          !getAdsBackground().adId ? (
            <>
              <a
                href={
                  UserBackgroundCoverImage
                    ? undefined
                    : getAdsBackground().redirectUrl ||
                      getRandBackgroundRedirectUrl() ||
                      undefined
                }
                target="_blank"
                rel="noreferrer"
                className="home-bgd-container"
                style={{
                  height:
                    getAdsBackground().adId && !UserBackgroundCoverImage
                      ? bgHeight || "100vh"
                      : "100vh",
                }}
              >
                {getAdsBackground()?.fileType === "Video" ? (
                  <AutoPlayVideo
                    src={backgroundCoverImage}
                    onClick={handleBannerClick}
                    height="100%"
                  />
                ) : backgroundCoverImage ? (
                  <>
                    <Image
                      src={backgroundCoverImage}
                      ext="jpg"
                      onClick={handleBannerClick}
                      className="home-bgd-image"
                      useMap="#bgdMap"
                      onLoad={(e) => {
                        if (
                          (getAdsBackground().adId &&
                            getAdsBackground().isBlocked) ||
                          IS_PREVIEW
                        ) {
                          setBgHeight(e.target.height);
                        }
                        setIsImageLoaded(true);
                      }}
                      style={
                        (getAdsBackground()?.adId &&
                          getAdsBackground()?.isBlocked &&
                          !UserBackgroundCoverImage) ||
                        (IS_PREVIEW && !UserBackgroundCoverImage)
                          ? {
                              maxWidth: "100%",
                              position: mobileCondition
                                ? "absolute"
                                : undefined,
                              top: mobileCondition ? 0 : undefined,
                            }
                          : {
                              height: bgHeight || "100vh",
                            }
                      }
                    />
                    {/* {imgLinkCoords && isImageLoaded ? (
                      <map name="bgdMap">
                        <area
                          shape="rect"
                          coords={imgLinkCoords}
                          href={getAdsBackground().redirectUrl}
                          target="_blank"
                          rel="noreferrer"
                          title={getAdsBackground()?.description}
                          alt={getAdsBackground()?.description}
                          style={{ cursor: "pointer" }}
                        />
                      </map>
                    ) : null} */}
                  </>
                ) : null}
              </a>
            </>
          ) : (
            <AdBackground />
          )}
          {!conditionToHideArtistPageElement ? (
            <ArtistPageElement />
          ) : // show branding option for subsrubires, besides sub-users if topuser selected his branding for all
          isSubscribed() &&
            (!getUserData().IsSubUser ||
              !getUserData().TopUserBrandingEnabled) ? (
            <HomeBrandingElement />
          ) : null}
        </>
      ) : null}
      {!isSubscribed() && !getUserData().IsSubUser && <ThanksActionButton />}

      <div
        className={`home-upload-section-container${
          showMobileUploader ? " mobile-align-top" : ""
        }`}
      >
        {!showUploader ? (
          <DropZone />
        ) : (
          <UploaderContext.Provider
            value={{ uploadCounter: filesCount, filesProgress }}
          >
            <UploaderLogicWrapper>
              {mobileCondition ? (
                <MobileUploaderContainer />
              ) : (
                <UploaderComponent />
              )}
            </UploaderLogicWrapper>
          </UploaderContext.Provider>
        )}
      </div>
    </>
  );
};
